import { convertWeight, WeightUnit, WeightUnitChoices } from "@/fhir/weight";

export const prenatalFlowsheetPathMapping = {
  encounterFlowSheetWeight: "Weight",
  encounterFlowSheetWeightUnit: "Weight Unit",
  encounterFlowSheetFundalHeight: "Fundal Height",
  encounterFlowSheetFundalHeightUnit: "Fundal Height Unit",
};

export const ZOD_PATH_TO_SECTION_ID: Record<string, string> = {
  encounterFlowSheet: "prenatal-flowsheet-section",
  encounterVitalSigns: "vital-signs-section",
};

export interface FlowsheetWeightEntry {
  rowKey: string;
  weightInGrams: number | undefined;
  displayUnit: WeightUnit;
}

const roundToOneDecimal = (value: number): number => Math.round(value * 10) / 10;

/**
 * Weight change between each visit and the visit shown above it, expressed in that visit's own
 * unit. The flowsheet lists the newest visit first, so a visit is compared against the one that
 * came after it in time.
 *
 * A visit that recorded no weight is skipped, so the next visit that has one compares against the
 * nearest visit that recorded a weight. The newest visit, and any visit whose change rounds to
 * zero, get no entry in the map, so their rows render a single line.
 *
 * @param entriesNewestFirst visits in the table's display order, newest first
 * @returns changes keyed by `rowKey`, rounded to one decimal place
 */
export function calculateWeightChanges(
  entriesNewestFirst: FlowsheetWeightEntry[],
): Map<string, number> {
  const changes = new Map<string, number>();
  let previousInGrams: number | undefined;

  for (const entry of entriesNewestFirst) {
    if (entry.weightInGrams === undefined) continue;

    if (previousInGrams !== undefined) {
      const differenceInGrams = entry.weightInGrams - previousInGrams;
      const change = roundToOneDecimal(
        convertWeight(differenceInGrams, WeightUnitChoices.g, entry.displayUnit),
      );

      if (change !== 0) {
        changes.set(entry.rowKey, change);
      }
    }

    previousInGrams = entry.weightInGrams;
  }

  return changes;
}
